import React from 'react';

import { useQueryClient } from '@lumapps/base-api/react-query';
import { mdiPencil } from '@lumapps/lumx/icons';
import { Button, Emphasis, Kind, Message, Size, SkeletonRectangle, Text } from '@lumapps/lumx/react';
import { MessageWithActions } from '@lumapps/notifications/components/MessageWithActions';
import { isSuperAdmin } from '@lumapps/permissions';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { userDirectoryQueryKeys } from '../../api';
import { useGetPlatformUserDirectory } from '../../hooks/useGetPlatformUserDirectory';
import { USER_DIRECTORY } from '../../keys';
import type { UserDirectory } from '../../types';

export interface MainUserDirectoryEntryProps {
    setUserDirectoryToEdit: (userDirectory: UserDirectory) => void;
}

/**
 * Component responsible for displaying a header banner indicating the current "Main User Directory" status.
 */
export const MainUserDirectoryEntry: React.FC<MainUserDirectoryEntryProps> = ({ setUserDirectoryToEdit }) => {
    const { translateKey, translateAndReplaceWithComponents, translateObject } = useTranslate();
    const queryClient = useQueryClient();
    const isGlobalAdmin = useSelector(isSuperAdmin);

    const { data: userDirectory, isLoading, isError } = useGetPlatformUserDirectory({}, { enabled: isGlobalAdmin });

    if (!isGlobalAdmin) {
        return null;
    }

    if (isLoading) {
        return <SkeletonRectangle height={Size.l} />;
    }

    if (isError || !userDirectory) {
        return (
            <Message kind={Kind.info} hasBackground>
                <Text as="p">{translateKey(USER_DIRECTORY.NO_MAIN_DIRECTORY_MESSAGE)}</Text>
            </Message>
        );
    }

    const handleEditClick = async () => {
        await queryClient.invalidateQueries({ queryKey: userDirectoryQueryKeys.platformDirectories() });

        const freshMainUserDirectory = queryClient.getQueryData<UserDirectory>(
            userDirectoryQueryKeys.platformDirectory({}),
        );

        if (freshMainUserDirectory) {
            setUserDirectoryToEdit({ ...freshMainUserDirectory, isDefaultUserDirectory: true });
        }
    };

    return (
        <MessageWithActions
            actions={
                <Button emphasis={Emphasis.medium} leftIcon={mdiPencil} onClick={handleEditClick}>
                    {translateKey(USER_DIRECTORY.EDIT_MAIN_USER_DIRECTORY)}
                </Button>
            }
            kind={Kind.info}
            message={translateAndReplaceWithComponents(USER_DIRECTORY.MAIN_DIRECTORY_MESSAGE, {
                TITLE: <b>{translateObject(userDirectory.title) || translateKey(GLOBAL.UNTITLED)}</b>,
            })}
        />
    );
};
